import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.css';

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

export type TooltipBreadcrumbItem = {
  id: string;
  label: string;
  href?: string;
  onClick?: () => void;
};

type TooltipProps = {
  content: React.ReactNode;
  children?: React.ReactNode;
  /** `hover` wraps children; `icon` shows a help icon next to children (or alone). */
  trigger?: 'hover' | 'icon';
  placement?: TooltipPlacement;
  openDelayMs?: number;
  closeDelayMs?: number;
  disabled?: boolean;
  className?: string;
  iconLabel?: string;
  /** When true, the wrap stretches like a block flex child (e.g. around an h1). */
  asBlock?: boolean;
};

const VIEWPORT_PADDING = 8;
const GAP = 8;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function computePosition(
  triggerRect: DOMRect,
  tooltipRect: DOMRect,
  preferred: TooltipPlacement
): { top: number; left: number; placement: TooltipPlacement } {
  const placements: TooltipPlacement[] =
    preferred === 'top' || preferred === 'bottom'
      ? [preferred, preferred === 'top' ? 'bottom' : 'top', 'right', 'left']
      : [preferred, preferred === 'left' ? 'right' : 'left', 'bottom', 'top'];

  const candidates: Record<TooltipPlacement, { top: number; left: number }> = {
    top: {
      top: triggerRect.top - tooltipRect.height - GAP,
      left: triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2,
    },
    bottom: {
      top: triggerRect.bottom + GAP,
      left: triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2,
    },
    left: {
      top: triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2,
      left: triggerRect.left - tooltipRect.width - GAP,
    },
    right: {
      top: triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2,
      left: triggerRect.right + GAP,
    },
  };

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  for (const placement of placements) {
    const raw = candidates[placement];
    const left = clamp(
      raw.left,
      VIEWPORT_PADDING,
      viewportWidth - tooltipRect.width - VIEWPORT_PADDING
    );
    const top = clamp(
      raw.top,
      VIEWPORT_PADDING,
      viewportHeight - tooltipRect.height - VIEWPORT_PADDING
    );

    const fitsHorizontally =
      raw.left >= VIEWPORT_PADDING &&
      raw.left + tooltipRect.width <= viewportWidth - VIEWPORT_PADDING;
    const fitsVertically =
      raw.top >= VIEWPORT_PADDING &&
      raw.top + tooltipRect.height <= viewportHeight - VIEWPORT_PADDING;

    if (fitsHorizontally && fitsVertically) {
      return { top, left, placement };
    }
  }

  const fallback = candidates[preferred];
  return {
    placement: preferred,
    top: clamp(
      fallback.top,
      VIEWPORT_PADDING,
      viewportHeight - tooltipRect.height - VIEWPORT_PADDING
    ),
    left: clamp(
      fallback.left,
      VIEWPORT_PADDING,
      viewportWidth - tooltipRect.width - VIEWPORT_PADDING
    ),
  };
}

export function TooltipBreadcrumb({ items }: { items: TooltipBreadcrumbItem[] }) {
  return (
    <nav className="tooltip-breadcrumb" aria-label="Breadcrumb">
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const isInteractive = Boolean(item.href || item.onClick) && !isLast;

        return (
          <React.Fragment key={item.id}>
            {index > 0 ? (
              <span className="tooltip-breadcrumb-sep" aria-hidden="true">
                &gt;
              </span>
            ) : null}
            {isInteractive ? (
              item.href ? (
                <a className="tooltip-breadcrumb-link" href={item.href} onClick={item.onClick}>
                  {item.label}
                </a>
              ) : (
                <button type="button" className="tooltip-breadcrumb-link" onClick={item.onClick}>
                  {item.label}
                </button>
              )
            ) : (
              <span
                className={isLast ? 'tooltip-breadcrumb-current' : undefined}
                aria-current={isLast ? 'page' : undefined}
              >
                {item.label}
              </span>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

export function Tooltip({
  content,
  children,
  trigger = 'hover',
  placement = 'bottom',
  openDelayMs = 120,
  closeDelayMs = 180,
  disabled = false,
  className,
  iconLabel = 'More information',
  asBlock = false,
}: TooltipProps) {
  const tooltipId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const openTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
    placement: TooltipPlacement;
  } | null>(null);

  const clearTimers = useCallback(() => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleOpen = useCallback(() => {
    if (disabled) return;
    clearTimers();
    openTimerRef.current = window.setTimeout(() => {
      setIsOpen(true);
    }, openDelayMs);
  }, [clearTimers, disabled, openDelayMs]);

  const scheduleClose = useCallback(() => {
    clearTimers();
    closeTimerRef.current = window.setTimeout(() => {
      setIsOpen(false);
    }, closeDelayMs);
  }, [clearTimers, closeDelayMs]);

  const updatePosition = useCallback(() => {
    const triggerEl = wrapRef.current;
    const surfaceEl = surfaceRef.current;
    if (!triggerEl || !surfaceEl) return;

    const next = computePosition(
      triggerEl.getBoundingClientRect(),
      surfaceEl.getBoundingClientRect(),
      placement
    );
    setCoords(next);
  }, [placement]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setCoords(null);
      return;
    }
    updatePosition();
  }, [isOpen, content, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;

    const onReposition = () => updatePosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);

    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        clearTimers();
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [clearTimers, isOpen]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const wrapClassName = [
    'tooltip-trigger-wrap',
    asBlock ? 'tooltip-trigger-wrap-block' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const surface =
    isOpen && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={surfaceRef}
            id={tooltipId}
            role="tooltip"
            className="tooltip-surface"
            data-placement={coords?.placement ?? placement}
            style={{
              top: coords?.top ?? -9999,
              left: coords?.left ?? -9999,
              visibility: coords ? 'visible' : 'hidden',
            }}
            onMouseEnter={scheduleOpen}
            onMouseLeave={scheduleClose}
            onFocus={scheduleOpen}
            onBlur={scheduleClose}
          >
            {content}
          </div>,
          document.body
        )
      : null;

  if (trigger === 'icon') {
    return (
      <>
        <div className={wrapClassName} ref={wrapRef}>
          {children}
          <button
            type="button"
            className="tooltip-icon-button"
            aria-label={iconLabel}
            aria-describedby={isOpen ? tooltipId : undefined}
            data-tooltip-open={isOpen ? 'true' : 'false'}
            onMouseEnter={scheduleOpen}
            onMouseLeave={scheduleClose}
            onFocus={scheduleOpen}
            onBlur={scheduleClose}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              info
            </span>
          </button>
        </div>
        {surface}
      </>
    );
  }

  return (
    <>
      <div
        className={wrapClassName}
        ref={wrapRef}
        aria-describedby={isOpen ? tooltipId : undefined}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        onFocus={scheduleOpen}
        onBlur={scheduleClose}
      >
        {children}
      </div>
      {surface}
    </>
  );
}
